import { DATE_FORMAT } from "@/utils/date-time-utility";
import type { ToggleTabItem } from "@/components/ToggleTabs";
import type { Action } from "@/dummy-data/appointment-list-data-scheduling";
import { STATUS_APPOINTMENT } from "@/constants/master";
import type {
  AppointmentScope,
  GroupAppointmentStatus,
  GroupFilterChoice,
  GroupFilterState,
} from "./group-appointment-types";
import { CHART_COLORS } from "@/components/app/provider/patient/chart";

export const GROUP_APPOINTMENT_LABEL = {
  title: "Group Appointments",
  scheduleNew: "Schedule New Appointment",
  today: "Today",
  status: "Status",
  filter: "Filter",
  searchPlaceholder: "Search group appointments...",
} as const;

export const GROUP_APPOINTMENT_COLUMN_LABEL = {
  date: "Date",
  timeDuration: "Time & Duration",
  mode: "Mode",
  group: "Group",
  therapist: "Therapist",
  formStatus: "Form Status",
  status: "Status",
  action: "Action",
} as const;

export const APPOINTMENT_SCOPE = {
  INDIVIDUAL: "INDIVIDUAL",
  GROUP: "GROUP",
} as const;

export const APPOINTMENT_SCOPE_TABS: ToggleTabItem<AppointmentScope>[] = [
  { value: APPOINTMENT_SCOPE.INDIVIDUAL, label: "Individual" },
  { value: APPOINTMENT_SCOPE.GROUP, label: "Group" },
];

export const APPOINTMENT_MODE = {
  IN_PERSON: "IN_PERSON",
  VIRTUAL: "VIRTUAL",
} as const;

export const APPOINTMENT_MODE_LABEL: Record<string, string> = {
  [APPOINTMENT_MODE.IN_PERSON]: "In Person",
  [APPOINTMENT_MODE.VIRTUAL]: "Virtual",
};

export const FORM_STATUS = {
  SUBMITTED: "SUBMITTED",
  NOT_SUBMITTED: "NOT_SUBMITTED",
} as const;

export const FORM_STATUS_LABEL: Record<string, string> = {
  [FORM_STATUS.SUBMITTED]: "Submitted",
  [FORM_STATUS.NOT_SUBMITTED]: "Not Submitted",
};

export const GROUP_APPOINTMENT_STATUS = {
  SCHEDULED: "SCHEDULED",
  IN_EXAM: "IN_EXAM",
  COMPLETED: "COMPLETED",
  DOC_PENDING: "DOC_PENDING",
  CANCELLED: "CANCELLED",
} as const;

export const GROUP_APPOINTMENT_STATUS_LABEL: Record<string, string> = {
  [GROUP_APPOINTMENT_STATUS.SCHEDULED]: "Scheduled",
  [GROUP_APPOINTMENT_STATUS.IN_EXAM]: "In Exam",
  [GROUP_APPOINTMENT_STATUS.COMPLETED]: "Completed",
  [GROUP_APPOINTMENT_STATUS.DOC_PENDING]: "Doc Pending",
  [GROUP_APPOINTMENT_STATUS.CANCELLED]: "Cancelled",
};

/** Action chip label per session status — same labels/colours as the
   individual appointments Action column. */
export const GROUP_SESSION_ACTION_LABEL: Record<
  GroupAppointmentStatus,
  Action
> = {
  [GROUP_APPOINTMENT_STATUS.SCHEDULED]: "Scheduled",
  [GROUP_APPOINTMENT_STATUS.IN_EXAM]: "In Exam",
  [GROUP_APPOINTMENT_STATUS.COMPLETED]: "Signed Off",
  [GROUP_APPOINTMENT_STATUS.DOC_PENDING]: "Doc Pending",
  [GROUP_APPOINTMENT_STATUS.CANCELLED]: "Canceled",
};

/** Group's Status checkboxes are the individual list: same labels, colours and default ticks. */
export const GROUP_STATUS_FILTER_OPTIONS: GroupFilterChoice[] =
  STATUS_APPOINTMENT.map(({ value, label, color }) => ({
    value,
    label,
    color,
  }));

const GROUP_STATUS_DEFAULT_SELECTED: string[] = STATUS_APPOINTMENT.filter(
  (status) => status.selected
).map((status) => status.value);

/**
 * Session status each individual status filters by. Unmapped ones are sent as
 * is, so ticking only them matches no session rather than every session.
 */
export const INDIVIDUAL_TO_GROUP_STATUS: Record<
  string,
  GroupAppointmentStatus
> = {
  PENDING: GROUP_APPOINTMENT_STATUS.SCHEDULED,
  IN_EXAM: GROUP_APPOINTMENT_STATUS.IN_EXAM,
  COMPLETED: GROUP_APPOINTMENT_STATUS.COMPLETED,
  CANCELLED: GROUP_APPOINTMENT_STATUS.CANCELLED,
};

export const DATE_RANGE = {
  DAY: "DAY",
  WEEK: "WEEK",
  MONTH: "MONTH",
} as const;

export const DATE_RANGE_TABS = [
  { value: DATE_RANGE.DAY, label: "Day" },
  { value: DATE_RANGE.WEEK, label: "Week" },
  { value: DATE_RANGE.MONTH, label: "Month" },
];

/** Dot colours cycling per group, matching the design. */
export const GROUP_DOT_COLORS = [
  CHART_COLORS.badgeSuccessText,
  CHART_COLORS.btnSecondaryText,
  "#8B5CF6",
];

export const GROUP_APPOINTMENT_HEADER_DATE_FORMAT = "MMMM D, YYYY | dddd";
export const GROUP_APPOINTMENT_TIME_FORMAT = "h:mm A";
export const GROUP_APPOINTMENT_DURATION_SUFFIX = "Mins";
export const GROUP_APPOINTMENT_SEARCH_WIDTH = 240;
export const GROUP_DOT_SIZE = 8;

export const APPOINTMENT_DRAWER_LABEL = {
  title: "View Scheduled Group Appointment",
  checkInTitle: "Check-In",
  appointmentDetails: "Appointment Details",
  patientDetails: "Patient Details",
  location: "Location",
  scheduledTime: "Scheduled Time",
  therapist: "Therapist",
  coFacilitator: "Co-Facilitator",
  enrolled: "Enrolled",
  viewForms: "View Forms",
  hideForms: "Hide Forms",
  cancelAppointment: "Cancel Appointment",
  rescheduleAppointment: "Reschedule Appointment",
  completeCheckIn: "Complete Check-In",
  completeVisit: "Complete Visit",
  sessionSummary: "Session Summary",
  enrolledSuffix: "Patients",
  noPatients: "No patients enrolled",
} as const;

export const CANCEL_SESSION_CONFIRMATION = {
  content: "Please confirm that you want to cancel this group appointment.",
  confirm: "Confirm",
  cancel: "Cancel",
} as const;

/** Same role gates as the individual appointment's Cancel / Reschedule actions. */
export const APPOINTMENT_ROLE = {
  SCHEDULING: "Appointment Scheduling",
  RESCHEDULE_AND_CANCEL: "Reschedule and Cancel appointment",
} as const;

export const GROUP_RESCHEDULE_DRAWER_WIDTH = "800px";
export const GROUP_RESCHEDULE_SLOT_FORMAT = `${DATE_FORMAT}, hh:mm A`;
export const GROUP_RESCHEDULE_END_FORMAT = "hh:mm A";
export const GROUP_RESCHEDULE_LOCATION_PAGINATION = {
  page: 0,
  pageSize: 20,
} as const;

export const GROUP_RESCHEDULE_LABEL = {
  title: "Reschedule Appointment",
  save: "Save And Close",
  groupName: "Group Name",
  therapist: "Therapist",
  coFacilitator: "Co-Facilitator",
  status: "Appointment Status",
  visitType: "Visit Type",
  enrolled: "Enrolled",
  currentSlot: "Current Slot",
  location: "Location",
  locationPlaceholder: "Select Location",
  dateTime: "Date & Time",
  selectLocationFirst: "Select a location to view available slots",
} as const;

export const GROUP_RESCHEDULE_MESSAGE = {
  noAppointments: "No patient appointments to reschedule",
  partialFailure:
    "Some patient appointments could not be rescheduled. Please try again.",
} as const;

/** Individual appointment lists that also show the rescheduled patient appointments. */
export const INDIVIDUAL_APPOINTMENT_QUERY_KEYS = [
  "appointment-list",
  "appointment-list-v2",
  "future-appointment",
] as const;

/** Query param the calendar carries the Individual / Group scope in. */
export const SCOPE_QUERY_PARAM = "scope";

/** Query params the sessions list is filtered by. */
export const GROUP_TYPE_QUERY_PARAM = "groupType";
export const THERAPY_CATEGORY_QUERY_PARAM = "therapyCategory";

export const GROUP_SESSION_FILTER_LABEL = {
  groupType: "Group Type",
  therapyCategory: "Therapy Category",
  allGroupTypes: "All group types",
  allTherapyCategories: "All therapy categories",
} as const;

/** Query param the check-in and session-note pages read the session from. */
export const SESSION_ID_QUERY_PARAM = "sessionId";

export const APPOINTMENT_DRAWER_WIDTH = "520px";
export const APPOINTMENT_DRAWER_TIME_FORMAT = `${DATE_FORMAT}, hh:mm A`;
export const APPOINTMENT_DETAIL_SEPARATOR = ":";

export const GROUP_SESSION_DEFAULT_SORT_BY = "start_time";
export const GROUP_SESSION_DEFAULT_SORT_DIRECTION = "asc";

/** Checkbox sections Group shows in place of the individual ones; keys are the titles. */
export const GROUP_FILTER_KEY = {
  LOCATIONS: "Locations",
  STATUS: "Status",
} as const;

export const GROUP_FILTER_DEFAULT_STATE: GroupFilterState = {
  [GROUP_FILTER_KEY.LOCATIONS]: { selected: [], isExpand: false },
  [GROUP_FILTER_KEY.STATUS]: {
    selected: GROUP_STATUS_DEFAULT_SELECTED,
    isExpand: false,
  },
};

export const PATIENT_FORMS_LABEL = {
  previewForm: "Preview Form",
  notSubmitted: "-",
  loading: "Loading forms...",
  loadError: "Unable to load forms",
} as const;

export const CUSTOM_FORMS_SEPARATOR = ",";
